import { useState, useEffect, useCallback } from 'react';
import { getPointsTable, getPlayerStars, PointsTableRow, PlayerStarRow } from '@/lib/supabase';

type Tab = 'points' | 'defenders' | 'liberos' | 'setters' | 'blockers' | 'finishers' | 'acers' | 'overall' | 'mvp';

interface TournamentStatsModalProps {
  tournamentId: string;
  tournamentName?: string;
  onClose: () => void;
}

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
  { key: 'points', label: 'Points', icon: <span className="text-xs">📊</span> },
  { key: 'defenders', label: 'Defenders', icon: <span className="text-xs">🛡️</span> },
  { key: 'liberos', label: 'Liberos', icon: <span className="text-xs">🔄</span> },
  { key: 'setters', label: 'Setters', icon: <span className="text-xs">🎯</span> },
  { key: 'blockers', label: 'Blockers', icon: <span className="text-xs">🧱</span> },
  { key: 'finishers', label: 'Finishers', icon: <span className="text-xs">💥</span> },
  { key: 'acers', label: 'Acers', icon: <span className="text-xs">⚡</span> },
  { key: 'overall', label: 'Overall', icon: <span className="text-xs">⭐</span> },
  { key: 'mvp', label: 'MVP', icon: <span className="text-xs">🏆</span> },
];

export default function TournamentStatsModal({ tournamentId, tournamentName, onClose }: TournamentStatsModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('points');
  const [pointsRows, setPointsRows] = useState<PointsTableRow[]>([]);
  const [playerRows, setPlayerRows] = useState<PlayerStarRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[TournamentStatsModal] Loading stats for tournament:', tournamentId);
      const [pts, players] = await Promise.all([
        getPointsTable(tournamentId),
        getPlayerStars(tournamentId),
      ]);
      console.log('[TournamentStatsModal] Points table rows:', pts.length);
      console.log('[TournamentStatsModal] Player stars rows:', players.length);
      setPointsRows(pts);
      setPlayerRows(players);
    } catch (err: any) {
      console.error('[TournamentStatsModal] Failed to load statistics:', err);
      // Show the actual error message with details
      const errorMsg = err?.message || err?.details || err?.hint || (err instanceof Error ? err.message : 'Failed to load statistics');
      setError(`Failed to load statistics: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => { load(); }, [load]);

  // Pre-sorted views for each player tab
  const defenders = [...playerRows].filter(r => (r.defenderStars ?? 0) > 0).sort((a, b) => (b.defenderStars ?? 0) - (a.defenderStars ?? 0));
  const liberos = [...playerRows].filter(r => (r.liberoStars ?? 0) > 0).sort((a, b) => (b.liberoStars ?? 0) - (a.liberoStars ?? 0));
  const setters = [...playerRows].filter(r => (r.setterStars ?? 0) > 0).sort((a, b) => (b.setterStars ?? 0) - (a.setterStars ?? 0));
  const blockers = [...playerRows].filter(r => (r.blockerStars ?? 0) > 0).sort((a, b) => (b.blockerStars ?? 0) - (a.blockerStars ?? 0));
  const finishers = [...playerRows].filter(r => (r.finisherStars ?? 0) > 0).sort((a, b) => (b.finisherStars ?? 0) - (a.finisherStars ?? 0));
  const acers = [...playerRows].filter(r => r.acerStars > 0).sort((a, b) => b.acerStars - a.acerStars);
  const overall = [...playerRows].filter(r => r.totalStars > 0).sort((a, b) => b.totalStars - a.totalStars || (b.blockerStars ?? 0) - (a.blockerStars ?? 0));

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* Header */}
      <div className="bg-surface border-b border-border px-4 py-3 flex items-center gap-3 safe-top">
        <button onClick={onClose} className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-raised transition-colors" aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-bold text-text-primary truncate">Tournament Statistics</h2>
          {tournamentName && <p className="text-[10px] text-text-muted truncate">{tournamentName}</p>}
        </div>
        <button onClick={load} className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-raised transition-colors" aria-label="Refresh" title="Refresh">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-surface border-b border-border overflow-x-auto">
        <div className="flex min-w-max px-2">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-2.5 text-xs font-semibold whitespace-nowrap transition-colors border-b-2 ${
                activeTab === tab.key
                  ? 'border-warning text-warning'
                  : 'border-transparent text-text-muted hover:text-text-primary'
              }`}
            >
              <span className="mr-1">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-2 py-3">
        {error && (
          <div className="mx-2 bg-error/15 border border-error/30 rounded-lg p-3 mb-3">
            <p className="text-error text-sm">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeTab === 'points' ? (
          <PointsTab rows={pointsRows} />
        ) : activeTab === 'defenders' ? (
          <PlayerStarsTab rows={defenders} type="defender" label="Defender Stars" />
        ) : activeTab === 'liberos' ? (
          <PlayerStarsTab rows={liberos} type="libero" label="Libero Stars" />
        ) : activeTab === 'setters' ? (
          <PlayerStarsTab rows={setters} type="setter" label="Setter Stars" />
        ) : activeTab === 'blockers' ? (
          <PlayerStarsTab rows={blockers} type="blocker" label="Blocker Stars" />
        ) : activeTab === 'finishers' ? (
          <PlayerStarsTab rows={finishers} type="finisher" label="Finisher Stars" />
        ) : activeTab === 'acers' ? (
          <PlayerStarsTab rows={acers} type="acer" label="Acer Stars" />
        ) : activeTab === 'overall' ? (
          <OverallTab rows={overall} />
        ) : (
          <MVPTab rows={overall} />
        )}
      </div>
    </div>
  );
}

// ─── Points Table Tab ───
function PointsTab({ rows }: { rows: PointsTableRow[] }) {
  if (rows.length === 0) return <EmptyState title="No standings yet" subtitle="Complete league matches to see the points table." />;

  const positionStyle = (pos: number) => {
    if (pos === 1) return 'bg-warning/20 text-warning font-bold';
    if (pos === 2) return 'bg-surface-raised text-text-muted font-semibold';
    if (pos === 3) return 'bg-surface-raised/50 text-text-muted font-semibold';
    return 'text-text-muted';
  };

  const COLS = [
    { key: 'position',       label: '#',    title: 'Position' },
    { key: 'teamShortName',  label: 'Team', title: 'Team' },
    { key: 'wins',           label: 'W',    title: 'Wins' },
    { key: 'losses',         label: 'L',    title: 'Losses' },
    { key: 'points',         label: 'Pts',  title: 'Tournament Points' },
    { key: 'setsWon',        label: 'SW',   title: 'Sets Won' },
    { key: 'setsLost',       label: 'SL',   title: 'Sets Lost' },
    { key: 'setNet',         label: 'SNet', title: 'Set Net' },
    { key: 'setWinPct',      label: 'SW%',  title: 'Set Win %' },
    { key: 'pointsScored',   label: 'PF',   title: 'Points Won' },
    { key: 'pointsConceded', label: 'PA',   title: 'Points Lost' },
    { key: 'netPoints',      label: 'PNet', title: 'Points Net' },
    { key: 'pointsPct',      label: 'PTS%', title: 'Points %' },
  ];

  return (
    <div>
      <p className="text-[10px] text-text-muted bg-surface-raised border border-border rounded-lg px-3 py-2 mb-3">
        League matches only · Tiebreak: Points → Set Win% → Net Points
      </p>
      <div className="overflow-x-auto rounded-xl border border-border bg-surface">
        <table className="w-full text-xs border-collapse min-w-[640px]">
          <thead>
            <tr className="border-b border-border bg-surface-raised">
              {COLS.map(c => (
                <th key={c.key} title={c.title} className={`px-2 py-2.5 font-bold text-text-muted uppercase tracking-wide whitespace-nowrap ${c.key === 'teamShortName' ? 'text-left pl-3' : 'text-center'}`}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const setNet = row.setsWon - row.setsLost;
              const totalPts = row.pointsScored + row.pointsConceded;
              const pointsPct = totalPts > 0 ? (row.pointsScored / totalPts * 100).toFixed(1) : '0.0';
              return (
                <tr key={row.teamId} className={`border-b border-border/50 last:border-0 ${idx % 2 === 0 ? '' : 'bg-surface-raised/30'}`}>
                  <td className="text-center px-2 py-2.5">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] ${positionStyle(row.position)}`}>{row.position}</span>
                  </td>
                  <td className="pl-3 pr-2 py-2.5 text-left">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: row.teamColor }} />
                      <span className="font-bold text-text-primary whitespace-nowrap">{row.teamShortName}</span>
                    </div>
                  </td>
                  <td className="text-center px-2 py-2.5 font-semibold text-success">{row.wins}</td>
                  <td className="text-center px-2 py-2.5 text-text-secondary">{row.losses}</td>
                  <td className="text-center px-2 py-2.5 font-bold text-text-primary">{row.points}</td>
                  <td className="text-center px-2 py-2.5 text-text-secondary">{row.setsWon}</td>
                  <td className="text-center px-2 py-2.5 text-text-secondary">{row.setsLost}</td>
                  <td className={`text-center px-2 py-2.5 font-semibold ${setNet > 0 ? 'text-success' : setNet < 0 ? 'text-error' : 'text-text-secondary'}`}>{setNet > 0 ? `+${setNet}` : setNet}</td>
                  <td className="text-center px-2 py-2.5 text-text-secondary">{row.setWinPct.toFixed(1)}%</td>
                  <td className="text-center px-2 py-2.5 text-text-secondary">{row.pointsScored}</td>
                  <td className="text-center px-2 py-2.5 text-text-secondary">{row.pointsConceded}</td>
                  <td className={`text-center px-2 py-2.5 font-semibold ${row.netPoints > 0 ? 'text-success' : row.netPoints < 0 ? 'text-error' : 'text-text-secondary'}`}>{row.netPoints > 0 ? `+${row.netPoints}` : row.netPoints}</td>
                  <td className="text-center px-2 py-2.5 text-text-secondary">{pointsPct}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Player Stars Tabs (Defender / Libero / Setter / Blocker / Finisher / Acer) ───
function PlayerStarsTab({ rows, type, label }: { rows: PlayerStarRow[]; type: 'defender' | 'libero' | 'setter' | 'blocker' | 'finisher' | 'acer'; label: string }) {
  if (rows.length === 0) return <EmptyState title={`No ${label.toLowerCase()} yet`} subtitle="Stars are awarded from completed matches." />;

  const valueOf = (r: PlayerStarRow) => {
    switch (type) {
      case 'defender': return r.defenderStars ?? 0;
      case 'libero': return r.liberoStars ?? 0;
      case 'setter': return r.setterStars ?? 0;
      case 'blocker': return r.blockerStars ?? 0;
      case 'finisher': return r.finisherStars ?? 0;
      case 'acer': return r.acerStars ?? 0;
      default: return 0;
    }
  };

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-surface">
      <table className="w-full text-xs border-collapse min-w-[320px]">
        <thead>
          <tr className="border-b border-border bg-surface-raised">
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-left pl-3">Rank</th>
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-left">Player</th>
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-left">Team</th>
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-center pr-3">{label.split(' ')[0]}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.playerId} className={`border-b border-border/50 last:border-0 ${i % 2 === 0 ? '' : 'bg-surface-raised/30'}`}>
              <td className="pl-3 pr-2 py-2.5">
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold ${i === 0 ? 'bg-warning/20 text-warning' : 'bg-surface-raised text-text-muted'}`}>{i + 1}</span>
              </td>
              <td className="px-2 py-2.5 font-semibold text-text-primary whitespace-nowrap">{r.playerName}</td>
              <td className="px-2 py-2.5">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: r.teamColor }} />
                  <span className="text-text-secondary whitespace-nowrap">{r.teamShortName}</span>
                </div>
              </td>
              <td className="px-2 pr-3 py-2.5 text-center font-bold text-warning">{valueOf(r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Overall Stars Tab ───
function OverallTab({ rows }: { rows: PlayerStarRow[] }) {
  if (rows.length === 0) return <EmptyState title="No stars recorded" subtitle="Star data appears once matches are completed." />;

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-surface">
      <table className="w-full text-xs border-collapse min-w-[520px]">
        <thead>
          <tr className="border-b border-border bg-surface-raised">
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-left pl-3">Rank</th>
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-left">Player</th>
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-left">Team</th>
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-center" title="Acer">A</th>
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-center" title="Defender">D</th>
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-center" title="Libero">L</th>
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-center" title="Setter">S</th>
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-center" title="Blocker">B</th>
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-center" title="Finisher">F</th>
            <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-center pr-3">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.playerId} className={`border-b border-border/50 last:border-0 ${i % 2 === 0 ? '' : 'bg-surface-raised/30'}`}>
              <td className="pl-3 pr-2 py-2.5">
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold ${i === 0 ? 'bg-warning/20 text-warning' : 'bg-surface-raised text-text-muted'}`}>{i + 1}</span>
              </td>
              <td className="px-2 py-2.5 font-semibold text-text-primary whitespace-nowrap">{r.playerName}</td>
              <td className="px-2 py-2.5">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: r.teamColor }} />
                  <span className="text-text-secondary whitespace-nowrap">{r.teamShortName}</span>
                </div>
              </td>
              <td className="px-2 py-2.5 text-center text-success font-semibold">{r.acerStars || 0}</td>
              <td className="px-2 py-2.5 text-center text-warning">{r.defenderStars || 0}</td>
              <td className="px-2 py-2.5 text-center text-info">{r.liberoStars || 0}</td>
              <td className="px-2 py-2.5 text-center text-text-secondary">{r.setterStars || 0}</td>
              <td className="px-2 py-2.5 text-center text-team-b">{r.blockerStars || 0}</td>
              <td className="px-2 py-2.5 text-center text-team-a">{r.finisherStars || 0}</td>
              <td className="px-2 pr-3 py-2.5 text-center font-bold text-warning">{r.totalStars}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── MVP Tab ───
function MVPTab({ rows }: { rows: PlayerStarRow[] }) {
  if (rows.length === 0) return <EmptyState title="No MVP yet" subtitle="Tournament MVP is the player with the most total stars." />;

  const mvp = rows[0];

  return (
    <div>
      {/* MVP Crown Card */}
      <div className="card mb-4 text-center py-6 border-2 border-warning/30 bg-warning/5">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-warning/20 mb-3">
          <span className="text-2xl">🏆</span>
        </div>
        <p className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-1">Tournament MVP</p>
        <h3 className="text-lg font-bold text-text-primary mb-1">{mvp.playerName}</h3>
        <div className="flex items-center justify-center gap-1.5 mb-2">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: mvp.teamColor }} />
          <span className="text-sm text-text-secondary">{mvp.teamName}</span>
        </div>
        <p className="text-sm font-bold text-warning">{mvp.totalStars} Total Stars</p>
      </div>

      {/* Full rankings */}
      <p className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-2 px-1">MVP Rankings</p>
      <div className="overflow-x-auto rounded-xl border border-border bg-surface">
        <table className="w-full text-xs border-collapse min-w-[320px]">
          <thead>
            <tr className="border-b border-border bg-surface-raised">
              <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-left pl-3">Rank</th>
              <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-left">Player</th>
              <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-left">Team</th>
              <th className="px-2 py-2.5 font-bold text-text-muted uppercase text-center pr-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.playerId} className={`border-b border-border/50 last:border-0 ${i === 0 ? 'bg-warning/5' : ''} ${i % 2 === 1 ? 'bg-surface-raised/30' : ''}`}>
                <td className="pl-3 pr-2 py-2.5">
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold ${i === 0 ? 'bg-warning/20 text-warning' : 'bg-surface-raised text-text-muted'}`}>{i + 1}</span>
                </td>
                <td className="px-2 py-2.5 font-semibold text-text-primary whitespace-nowrap">{r.playerName}</td>
                <td className="px-2 py-2.5">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: r.teamColor }} />
                    <span className="text-text-secondary whitespace-nowrap">{r.teamShortName}</span>
                  </div>
                </td>
                <td className="px-2 pr-3 py-2.5 text-center font-bold text-warning">{r.totalStars}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Empty State ───
function EmptyState({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center px-6">
      <div className="w-14 h-14 rounded-full bg-surface-raised flex items-center justify-center mb-4">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-text-muted">
          <path d="M5 4h14M5 4v6a7 7 0 0 0 14 0V4M5 4H3M19 4h2M12 16v4M9 20h6" />
        </svg>
      </div>
      <p className="text-text-secondary text-sm font-semibold mb-1">{title}</p>
      <p className="text-text-muted text-xs">{subtitle}</p>
    </div>
  );
}
